import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Gauge,
  Building2,
  ChevronRight,
  Users,
  HardDrive,
  Wallet,
  TriangleAlert,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  tenantAdminService,
  TenantSummary,
  TenantUsage,
} from '@/features/branding/services/tenant-admin.service';
import { aiCreditsAdminService } from '@/features/branding/services/ai-credits.service';

export const Route = createFileRoute('/system/tenant-usage')({
  component: TenantUsagePage,
});

function formatBytes(bytes: number): string {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  return `${(bytes / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 2)} ${units[i]}`;
}

const transactionTypeLabel: Record<string, string> = {
  purchase: 'Compra',
  debit: 'Consumo',
  adjustment: 'Ajuste',
  refund: 'Estorno',
};

// ─── Formulário inline: Ajustar Créditos de IA (sem modal por cima do detalhe) ───

export function AdjustCreditsForm({ tenantId, onDone }: { tenantId: string; onDone: () => void }) {
  const qc = useQueryClient();
  const [operation, setOperation] = useState<'add' | 'remove'>('add');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');

  const adjustMutation = useMutation({
    mutationFn: () =>
      aiCreditsAdminService.adjustCredits(tenantId, {
        amountUsd: Number(amount),
        operation,
        description: description || undefined,
      }),
    onSuccess: (data) => {
      toast.success(`Saldo atualizado: US$ ${data.balanceUsd.toFixed(2)}`);
      qc.invalidateQueries({ queryKey: ['system', 'tenant-usage', tenantId] });
      onDone();
    },
  });

  const amountNum = Number(amount);
  const canSubmit = !isNaN(amountNum) && amountNum > 0;

  return (
    <form
      className="space-y-4 rounded-lg border border-gray-800 p-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (canSubmit) adjustMutation.mutate();
      }}
    >
      <h4 className="flex items-center gap-2 text-sm font-semibold">
        <Wallet className="h-4 w-4" />
        Ajustar Créditos de IA
      </h4>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="adjust-operation">Operação</Label>
          <Select value={operation} onValueChange={(v) => setOperation(v as 'add' | 'remove')}>
            <SelectTrigger id="adjust-operation">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="add">Adicionar créditos</SelectItem>
              <SelectItem value="remove">Remover créditos</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="adjust-amount">Valor (US$) *</Label>
          <Input
            id="adjust-amount"
            type="number"
            min="0.01"
            step="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="10.00"
          />
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="adjust-description">Observação</Label>
        <Input
          id="adjust-description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Cortesia, ajuste, etc."
        />
      </div>
      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onDone}>
          Cancelar
        </Button>
        <Button
          type="submit"
          variant={operation === 'remove' ? 'destructive' : 'default'}
          disabled={!canSubmit}
          loading={adjustMutation.isPending}
        >
          {operation === 'remove' ? 'Remover créditos' : 'Adicionar créditos'}
        </Button>
      </div>
    </form>
  );
}

// ─── Painel de uso de um tenant ────────────────────────────────────────────────

function TenantUsagePanel({ tenantId }: { tenantId: string }) {
  const [adjustOpen, setAdjustOpen] = useState(false);

  const { data: usage, isLoading } = useQuery<TenantUsage>({
    queryKey: ['system', 'tenant-usage', tenantId],
    queryFn: () => tenantAdminService.getTenantUsage(tenantId),
  });

  if (isLoading) return <p className="text-sm text-gray-400 py-4">Carregando uso…</p>;
  if (!usage) return null;

  const storagePct = usage.storage.limitBytes
    ? Math.min(100, (usage.storage.used / usage.storage.limitBytes) * 100)
    : 0;
  const usersPct = usage.users.limit
    ? Math.min(100, (usage.users.used / usage.users.limit) * 100)
    : 0;

  const breakdownEntries: { label: string; value: number }[] = [
    { label: 'Arquivos', value: usage.storage.breakdown.fileSystem },
    { label: 'Quadros', value: usage.storage.breakdown.boardFiles },
    { label: 'Documentos', value: usage.storage.breakdown.documents },
    { label: 'Tarefas diárias', value: usage.storage.breakdown.dailyTaskFiles },
    { label: 'Eventos de calendário', value: usage.storage.breakdown.calendarEventFiles },
  ];

  return (
    <div className="space-y-6">
      {/* Usuários */}
      <Card className="bg-gray-900 border-gray-800">
        <CardContent className="p-4 space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-indigo-400" />
              <span className="font-medium text-white text-sm">Usuários</span>
            </div>
            {usage.users.exceeded && (
              <Badge className="bg-red-700 text-white flex items-center gap-1">
                <TriangleAlert className="h-3 w-3" /> Limite excedido
              </Badge>
            )}
          </div>
          <p className="text-sm text-gray-300">
            {usage.users.used} / {usage.users.limit} usuários
          </p>
          <div className="h-2 rounded-full bg-gray-800 overflow-hidden">
            <div
              className={`h-full ${usage.users.exceeded ? 'bg-red-500' : 'bg-indigo-500'}`}
              style={{ width: `${usersPct}%` }}
            />
          </div>
        </CardContent>
      </Card>

      {/* Armazenamento */}
      <Card className="bg-gray-900 border-gray-800">
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <HardDrive className="h-4 w-4 text-indigo-400" />
              <span className="font-medium text-white text-sm">Armazenamento</span>
            </div>
            {usage.storage.exceeded && (
              <Badge className="bg-red-700 text-white flex items-center gap-1">
                <TriangleAlert className="h-3 w-3" /> Limite excedido
              </Badge>
            )}
          </div>
          <p className="text-sm text-gray-300">
            {formatBytes(usage.storage.used)} / {formatBytes(usage.storage.limitBytes)}
          </p>
          <div className="h-2 rounded-full bg-gray-800 overflow-hidden">
            <div
              className={`h-full ${usage.storage.exceeded ? 'bg-red-500' : 'bg-indigo-500'}`}
              style={{ width: `${storagePct}%` }}
            />
          </div>
          <div className="grid gap-2 pt-1 sm:grid-cols-2">
            {breakdownEntries.map((b) => (
              <div key={b.label} className="flex justify-between text-xs text-gray-400 px-2 py-1.5 rounded bg-gray-800/60">
                <span>{b.label}</span>
                <span className="text-gray-200 font-medium">{formatBytes(b.value)}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Créditos de IA */}
      <Card className="bg-gray-900 border-gray-800">
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Wallet className="h-4 w-4 text-indigo-400" />
              <span className="font-medium text-white text-sm">Créditos de IA</span>
              {usage.aiCredits.blocked && (
                <Badge className="bg-red-700 text-white">Bloqueado</Badge>
              )}
            </div>
            <Button
              size="sm"
              variant="outline"
              className="border-gray-700 text-gray-300"
              onClick={() => setAdjustOpen(true)}
            >
              Ajustar
            </Button>
          </div>

          <div className="grid gap-2 text-sm sm:grid-cols-3">
            <div className="text-center p-2 rounded bg-gray-800/60">
              <p className="text-gray-400 text-xs">Saldo</p>
              <p className={`font-medium ${usage.aiCredits.balanceUsd <= 0 ? 'text-red-400' : 'text-white'}`}>
                US$ {usage.aiCredits.balanceUsd.toFixed(2)}
              </p>
            </div>
            <div className="text-center p-2 rounded bg-gray-800/60">
              <p className="text-gray-400 text-xs">Total comprado</p>
              <p className="text-white font-medium">US$ {usage.aiCredits.totalPurchasedUsd.toFixed(2)}</p>
            </div>
            <div className="text-center p-2 rounded bg-gray-800/60">
              <p className="text-gray-400 text-xs">Total consumido</p>
              <p className="text-white font-medium">US$ {usage.aiCredits.totalSpentUsd.toFixed(2)}</p>
            </div>
          </div>

          {usage.aiCredits.spendingByModule.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-xs font-semibold text-gray-400">Consumo por módulo</p>
              <div className="divide-y divide-gray-800 rounded-lg border border-gray-800">
                {usage.aiCredits.spendingByModule.map((m) => (
                  <div key={m.module} className="flex justify-between px-3 py-1.5 text-sm">
                    <span className="text-gray-300">{m.module}</span>
                    <span className="text-gray-200">
                      US$ {m.totalUsd.toFixed(2)}{' '}
                      <span className="text-gray-500 text-xs">({m.count}x)</span>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {usage.aiCredits.recentTransactions.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-xs font-semibold text-gray-400">Últimas transações</p>
              <div className="divide-y divide-gray-800 rounded-lg border border-gray-800 max-h-56 overflow-y-auto">
                {usage.aiCredits.recentTransactions.map((t) => {
                  const value = Number(t.amountUsd);
                  return (
                    <div key={t.id} className="flex items-center justify-between px-3 py-1.5 text-sm">
                      <div>
                        <p className="text-gray-200">
                          {transactionTypeLabel[t.type] ?? t.type}
                          {t.module ? ` · ${t.module}` : ''}
                        </p>
                        {t.description && (
                          <p className="text-xs text-gray-500">{t.description}</p>
                        )}
                      </div>
                      <span className={value < 0 ? 'text-red-400' : 'text-green-400'}>
                        {value < 0 ? '' : '+'}US$ {value.toFixed(2)}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {adjustOpen && <AdjustCreditsForm tenantId={tenantId} onDone={() => setAdjustOpen(false)} />}
    </div>
  );
}

// ─── Página principal ───────────────────────────────────────────────────────────

function TenantUsagePage() {
  const [selectedTenant, setSelectedTenant] = useState<TenantSummary | null>(null);

  const { data: tenants = [], isLoading } = useQuery({
    queryKey: ['system', 'tenants'],
    queryFn: () => tenantAdminService.listTenants(),
  });

  if (isLoading) {
    return <div className="p-6 text-gray-400">Carregando tenants…</div>;
  }

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white flex items-center gap-2">
          <Gauge className="h-6 w-6 text-indigo-400" />
          Uso por Tenant
        </h1>
        <p className="text-gray-400 mt-1">
          Acompanhe o consumo de usuários, armazenamento e créditos de IA de cada cliente.
        </p>
      </div>

      <div className="grid gap-3">
        {tenants.map((tenant) => (
          <Card
            key={tenant.tenantId}
            className="cursor-pointer bg-gray-900 border-gray-800 hover:border-indigo-500/50 transition-colors"
            onClick={() => setSelectedTenant(tenant)}
          >
            <CardContent className="flex items-center justify-between py-4 px-5">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-lg bg-gray-800 flex items-center justify-center">
                  <Building2 className="h-5 w-5 text-gray-400" />
                </div>
                <div>
                  <p className="font-medium text-white leading-none">{tenant.tenantName}</p>
                  <p className="text-xs text-gray-500 mt-1 font-mono">{tenant.tenantSlug}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Badge
                  className={
                    tenant.active ? 'bg-green-600 text-white' : 'bg-gray-700 text-gray-300'
                  }
                >
                  {tenant.active ? 'Ativo' : 'Inativo'}
                </Badge>
                <ChevronRight className="h-4 w-4 text-gray-500" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {selectedTenant && (
        <Dialog open onOpenChange={(open) => !open && setSelectedTenant(null)}>
          <DialogContent size="xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                {selectedTenant.tenantName}
                <span className="text-xs text-muted-foreground font-mono font-normal">
                  {selectedTenant.tenantSlug}
                </span>
              </DialogTitle>
            </DialogHeader>

            <TenantUsagePanel tenantId={selectedTenant.tenantId} />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
